import { useEffect, useState } from 'react';
import { DEMO, installExportInterceptor } from './demo';
import { ApiError, auth, type Brand, type User } from './api';
import { Banner, Field, Spinner } from './components/Common';
import { Catalogue } from './pages/Catalogue';
import { Dashboard } from './pages/Dashboard';
import { RunReport } from './pages/RunReport';
import { History } from './pages/History';
import { Connections } from './pages/Connections';
import { Requests } from './pages/Requests';
import { ReportEditor } from './pages/ReportEditor';
import { Schedules } from './pages/Schedules';
import { Lookups } from './pages/Lookups';
import { Datasets } from './pages/Datasets';
import { Users } from './pages/Users';
import { Audit } from './pages/Audit';
import { Health } from './pages/Health';
import { DirectorShell } from './pages/Director';

/**
 * Hash routing, deliberately.
 *
 * It means the platform can be served from any path behind whatever reverse
 * proxy the company already runs, with no server rewrite rules and no
 * history-API fallback to misconfigure. For an internal tool of this size the
 * cost of a router dependency is not worth paying.
 */
function useRoute(): [string[], (path: string) => void] {
  const [hash, setHash] = useState(() => window.location.hash.replace(/^#\/?/, ''));
  useEffect(() => {
    const onChange = () => setHash(window.location.hash.replace(/^#\/?/, ''));
    window.addEventListener('hashchange', onChange);
    return () => window.removeEventListener('hashchange', onChange);
  }, []);
  const navigate = (path: string) => {
    window.location.hash = `#/${path.replace(/^\/+/, '')}`;
  };
  return [hash.split('/').filter(Boolean), navigate];
}

/**
 * Preview banner. Rendered only in the standalone single-file build, and worth
 * the few lines: someone clicking round a convincing interface should never be
 * in any doubt about whether they are looking at real live data.
 */
function PreviewBanner() {
  if (!DEMO) return null;
  // The label is set when the file is built. A preview of REAL figures must not
  // read like a demo of invented ones, and vice versa — getting that wrong is
  // how a sample number ends up in a board pack.
  const label =
    (window as any).__DEMO_FIXTURE__?.LABEL ??
    'Sample people and figures, not live Bullhorn or Dialpad data.';
  return (
    <div className="preview-banner">
      <strong>Preview</strong>
      <span>
        {label} This is the real interface running on a recording of real engine output — reports, charts, warnings and
        the PDF/Excel exports are genuine. Saving, syncing and sending are switched off, because there is no server
        behind this file.
      </span>
    </div>
  );
}

/**
 * Which interface to show: the full platform or the director view.
 *
 * - A viewer with director view only ever gets the director view.
 * - Someone with director view AND a build role lands there, and can step out.
 * - An administrator can open the director view as a preview, to see exactly
 *   what Mike and Thomas see (admins already see every report, as directors do).
 * Remembered per browser tab only, so the next sign-in starts from the default.
 */
type ViewMode = 'director' | 'full';
const VIEW_KEY = 'scg.view';
function readViewMode(): ViewMode | null {
  try {
    const v = sessionStorage.getItem(VIEW_KEY);
    return v === 'director' || v === 'full' ? v : null;
  } catch {
    return null;
  }
}
function writeViewMode(mode: ViewMode): void {
  try {
    sessionStorage.setItem(VIEW_KEY, mode);
  } catch {
    /* storage blocked - the switch still works for this page */
  }
}
function canPreviewDirector(user: User): boolean {
  return user.directorView || user.role === 'admin';
}

export function App() {
  const [user, setUser] = useState<User | null>(null);
  const [viewChoice, setViewChoice] = useState<ViewMode | null>(readViewMode);
  const [brand, setBrand] = useState<Brand | null>(null);
  const [booting, setBooting] = useState(true);

  useEffect(() => {
    if (DEMO) installExportInterceptor();
    auth
      .me()
      .then(({ user: u, brand: b }) => {
        setUser(u);
        setBrand(b);
      })
      .catch(() => undefined)
      .finally(() => setBooting(false));
  }, []);

  if (booting) {
    return (
      <div className="login-wrap">
        <Spinner label="Loading…" />
      </div>
    );
  }

  if (!user) {
    return (
      <Login
        onSignedIn={(u, b) => {
          setUser(u);
          setBrand(b);
        }}
      />
    );
  }

  if (user.mustChangePassword) {
    return <ForcePasswordChange user={user} onDone={() => setUser({ ...user, mustChangePassword: false })} />;
  }

  const switchView = (mode: ViewMode) => {
    writeViewMode(mode);
    setViewChoice(mode);
    window.location.hash = mode === 'full' ? '#/dashboard' : '#/reports';
  };

  const mode: ViewMode =
    user.directorView && user.role === 'viewer'
      ? 'director'
      : canPreviewDirector(user) && (viewChoice ?? (user.directorView ? 'director' : 'full')) === 'director'
        ? 'director'
        : 'full';

  if (mode === 'director') {
    return (
      <>
        <PreviewBanner />
        <DirectorShell
          user={user}
          brand={brand}
          onSignOut={() => setUser(null)}
          onFullPlatform={user.role !== 'viewer' ? () => switchView('full') : undefined}
          previewing={!user.directorView}
        />
      </>
    );
  }

  return (
    <>
      <PreviewBanner />
      <Shell
        user={user}
        brand={brand}
        onSignOut={() => setUser(null)}
        onDirectorView={canPreviewDirector(user) ? () => switchView('director') : undefined}
      />
    </>
  );
}

// ------------------------------------------------------------------ login -----

function Login({ onSignedIn }: { onSignedIn: (user: User, brand: Brand) => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Read from /api/health rather than the brand object, which only exists once
  // someone has signed in — which is precisely who this signpost is not for.
  const [myScgUrl, setMyScgUrl] = useState('');

  useEffect(() => {
    fetch('/api/health')
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => setMyScgUrl(typeof j?.myScgUrl === 'string' ? j.myScgUrl : ''))
      .catch(() => setMyScgUrl(''));
  }, []);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { user, brand } = await auth.login(email, password);
      onSignedIn(user, brand);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not sign in.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login-wrap">
      <form className="login-card" onSubmit={submit}>
        <div className="head">
          <BrandLogo variant="purple" />
          <div className="org">Spencer Clarke Group</div>
          <h1>Reporting platform</h1>
        </div>
        <div className="body">
          {error && <Banner tone="error">{error}</Banner>}
          <Field label="Work email">
            <input type="email" value={email} autoComplete="username" autoFocus required onChange={(e) => setEmail(e.target.value)} />
          </Field>
          <Field label="Password">
            <input type="password" value={password} autoComplete="current-password" required onChange={(e) => setPassword(e.target.value)} />
          </Field>
          <button className="primary" type="submit" disabled={busy} style={{ width: '100%', marginTop: 4 }}>
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
          <p style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 14, marginBottom: 0 }}>
            Internal use only. Every report run is recorded against your account.
          </p>
          {/*
            Most people reach this platform from the My SCG sidebar and have never
            set a password here at all — so "forgotten your password" almost always
            means their My SCG one. Sending them to the hub's reset form is the
            difference between a working door and a dead end. Hidden when
            MY_SCG_URL is unset, rather than linking somewhere that does not exist.
          */}
          {myScgUrl ? (
            <p style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 10, marginBottom: 0, lineHeight: 1.55 }}>
              <strong style={{ color: 'var(--body)', fontWeight: 600 }}>Most people sign in through My SCG.</strong>{' '}
              Forgotten your password? Reset it at{' '}
              <a href={`${myScgUrl.replace(/\/+$/, '')}/forgot`} style={{ color: 'var(--lilac-dark)', fontWeight: 500 }}>
                My SCG
              </a>
              , then use the SCG Reporting link in the sidebar — no separate password needed.
            </p>
          ) : null}
          {/*
            There is deliberately no "forgot password" link. A reset link needs a
            mail server, and this platform is routinely run without one — an
            emailed reset that silently goes nowhere is worse than no link at
            all. Resets are done by whoever runs the server, which is also the
            correct authority for it. Saying so here saves the confusion of
            looking for a link that was never going to work.
          */}
          <details style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 8 }}>
            <summary style={{ cursor: 'pointer' }}>Forgotten a local password?</summary>
            <p style={{ margin: '6px 0 0' }}>
              There is no reset email — this platform often runs without a mail server, and a reset that quietly goes
              nowhere would be worse than none. Ask whoever runs the server to issue you a new one. On the machine
              running it, in the platform folder:
            </p>
            <code style={{ display: 'block', marginTop: 6, padding: '6px 8px', fontSize: 11, wordBreak: 'break-all' }}>
              npm run admin -- reset-password you@spencerclarkegroup.co.uk
            </code>
          </details>
        </div>
      </form>
    </div>
  );
}

function ForcePasswordChange({ user, onDone }: { user: User; onDone: () => void }) {
  const [currentPassword, setCurrent] = useState('');
  const [newPassword, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (newPassword !== confirm) {
      setError('The two new passwords do not match.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await auth.changePassword(currentPassword, newPassword);
      // Changing a password drops every session, this one included.
      await auth.login(user.email, newPassword);
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not change the password.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login-wrap">
      <form className="login-card" onSubmit={submit}>
        <div className="head">
          <BrandLogo variant="purple" />
          <div className="org">Spencer Clarke Group</div>
          <h1>Choose a password</h1>
        </div>
        <div className="body">
          <Banner tone="info">Your account was set up with a temporary password. Choose your own before continuing.</Banner>
          {error && <Banner tone="error">{error}</Banner>}
          <Field label="Temporary password">
            <input type="password" value={currentPassword} required onChange={(e) => setCurrent(e.target.value)} />
          </Field>
          <Field label="New password" help="At least 12 characters. Length matters more than symbols.">
            <input type="password" value={newPassword} required minLength={12} onChange={(e) => setNext(e.target.value)} />
          </Field>
          <Field label="Confirm new password">
            <input type="password" value={confirm} required onChange={(e) => setConfirm(e.target.value)} />
          </Field>
          <button className="primary" type="submit" disabled={busy} style={{ width: '100%' }}>
            {busy ? 'Saving…' : 'Save and continue'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ------------------------------------------------------------------ shell -----

const NAV = [
  { group: 'Reporting', items: [
    { path: 'dashboard', label: 'Dashboard', role: 'viewer' },
    { path: 'reports', label: 'Reports', role: 'viewer' },
    { path: 'history', label: 'Run history', role: 'viewer' },
  ] },
  // "Stored data" and "Managed lists" were viewer-visible. They are plumbing --
  // sync schedules, retention, the fee-earner include-list -- and a director
  // landing on either learns nothing and can break something. Raised to editor
  // so the reporting group is the whole of a director's world.
  { group: 'Build', items: [
    { path: 'requests', label: 'Requests', role: 'editor' },
    { path: 'datasets', label: 'Stored data', role: 'editor' },
    { path: 'editor', label: 'Report builder', role: 'editor' },
    { path: 'schedules', label: 'Schedules', role: 'editor' },
    { path: 'lists', label: 'Managed lists', role: 'editor' },
  ] },
  { group: 'Administration', items: [
    { path: 'connections', label: 'Entry points', role: 'editor' },
    { path: 'health', label: 'System health', role: 'editor' },
    { path: 'users', label: 'People', role: 'admin' },
    { path: 'audit', label: 'Audit log', role: 'admin' },
  ] },
] as const;

const RANK = { viewer: 1, editor: 2, admin: 3 } as const;

function Shell({
  user,
  brand,
  onSignOut,
  onDirectorView,
}: {
  user: User;
  brand: Brand | null;
  onSignOut: () => void;
  onDirectorView?: () => void;
}) {
  const [route, navigate] = useRoute();
  const section = route[0] ?? 'dashboard';

  useEffect(() => {
    if (!window.location.hash) navigate('dashboard');
  }, []);

  const allowed = (role: string) => RANK[user.role] >= RANK[role as keyof typeof RANK];

  let body: React.ReactNode;
  switch (section) {
    case 'dashboard':
      body = <Dashboard user={user} onOpenReports={() => navigate('reports')} />;
      break;
    case 'reports':
      body = route[1] ? <RunReport slug={route[1]} user={user} /> : <Catalogue onOpen={(slug) => navigate(`reports/${slug}`)} />;
      break;
    case 'runs':
      body = <RunReport runId={route[1]} user={user} />;
      break;
    case 'history':
      body = <History onOpenRun={(id) => navigate(`runs/${id}`)} />;
      break;
    case 'requests':
      body = allowed('editor') ? <Requests /> : <Denied />;
      break;
    case 'editor':
      body = allowed('editor') ? <ReportEditor slug={route[1]} onNavigate={navigate} /> : <Denied />;
      break;
    case 'schedules':
      body = allowed('editor') ? <Schedules /> : <Denied />;
      break;
    case 'lists':
      body = allowed('editor') ? <Lookups slug={route[1]} canEdit={allowed('editor')} onNavigate={navigate} /> : <Denied />;
      break;
    case 'datasets':
      body = allowed('editor') ? <Datasets slug={route[1]} canEdit={allowed('editor')} onNavigate={navigate} /> : <Denied />;
      break;
    case 'connections':
      body = allowed('editor') ? <Connections isAdmin={user.role === 'admin'} /> : <Denied />;
      break;
    case 'health':
      body = allowed('editor') ? <Health /> : <Denied />;
      break;
    case 'users':
      body = allowed('admin') ? <Users /> : <Denied />;
      break;
    case 'audit':
      body = allowed('admin') ? <Audit /> : <Denied />;
      break;
    default:
      body = <Denied notFound />;
  }

  return (
    <div className="shell">
      <aside className="sidebar">
        <div className="wordmark">
          {/* The white logo only, per the hub — no wordmark text beside it. */}
          <BrandLogo variant="white" name={brand?.name} />
        </div>
        <nav aria-label="Main">
          {NAV.map((group) => {
            const items = group.items.filter((item) => allowed(item.role));
            if (!items.length) return null;
            return (
              <div key={group.group}>
                <div className="nav-group">{group.group}</div>
                {items.map((item) => (
                  <a
                    key={item.path}
                    className={`nav-item${section === item.path ? ' active' : ''}`}
                    href={`#/${item.path}`}
                  >
                    {item.label}
                  </a>
                ))}
              </div>
            );
          })}
          {/* What Mike and Thomas see. A preview for an administrator; for
              someone who has director view themselves, the way back to it. */}
          {onDirectorView ? (
            <div>
              <div className="nav-group">Preview</div>
              <a
                className="nav-item"
                href="#/reports"
                onClick={(e) => {
                  e.preventDefault();
                  onDirectorView();
                }}
              >
                Director view
              </a>
            </div>
          ) : null}
        </nav>
        <div className="spacer" />
        <div className="who">
          <b>{user.name}</b>
          <span>{user.email}</span>
          <div>
            <span className="role">{user.role}</span>
          </div>
          <button
            className="ghost small"
            style={{ marginTop: 10, color: 'rgba(255,255,255,.8)' }}
            onClick={async () => {
              await auth.logout();
              onSignOut();
            }}
          >
            Sign out
          </button>
        </div>
        {/* Staff arrive here from the hub, so the way back belongs in the chrome.
            Hidden when MY_SCG_URL is unset rather than pointing at nothing. */}
        {brand?.myScgUrl ? (
          <a className="back-link" href={brand.myScgUrl}>
            ← Back to My SCG
          </a>
        ) : null}
      </aside>
      <main className="main">
        {/* The hub's signature top bar: lilac greeting, avatar right. Each page
            renders its own title beneath, so the greeting is written once here
            rather than threaded through twelve page components. */}
        <div className="topbar">
          <div className="greet">{greeting()}, {user.name.split(' ')[0]}</div>
          <div className="avatar-chip">
            <div className="avatar-circle">{initials(user.name)}</div>
            <span>{user.name}</span>
          </div>
        </div>
        {body}
      </main>
    </div>
  );
}

/**
 * The SCG wordmark, with a text fallback if the asset is missing.
 *
 * Worth the component rather than a bare <img>: a missing logo on the purple
 * sidebar is invisible rather than obviously broken, so the app would look like
 * it had simply lost its branding. The fallback makes the failure legible.
 */
function BrandLogo({ variant, name }: { variant: 'white' | 'purple'; name?: string }) {
  const [failed, setFailed] = useState(false);
  const label = name ?? 'Spencer Clarke Group';
  if (failed) return <div className="fallback">{label}</div>;
  return <img src={`/logo-${variant}.png`} alt={label} onError={() => setFailed(true)} />;
}

/** Europe/London, like every other time in this platform. */
function greeting(): string {
  const hour = Number(
    new Intl.DateTimeFormat('en-GB', { hour: 'numeric', hour12: false, timeZone: 'Europe/London' }).format(new Date()),
  );
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

function Denied({ notFound }: { notFound?: boolean }) {
  return (
    <div className="empty-state">
      <h3>{notFound ? 'Page not found' : 'You do not have access to this area'}</h3>
      <p>
        {notFound
          ? 'The link may be out of date.'
          : 'Building requests and editing entry points needs a higher role. Ask an administrator if you need it.'}
      </p>
    </div>
  );
}
